import { useEffect, useRef } from "react";
import { CanvasSourceSpecification, Layer, Source, useMap } from "react-map-gl/maplibre";

import { Raster, RasterStyle } from "@nens/lizard/api-v4";
import { useAnimationFrame } from "@nens/react/hooks";
import { ImageCache, arrayMin } from "@nens/shared-utils";

import { makeWmsRequest } from "./animation-utils";
import { getOptimalImageDimensions } from "./image-dimensions";

/* tslint:disable */
/* eslint-disable */
type CanvasSourceAny = any;

/* Main idea:

   We use a react-map-gl Canvas-source and a raster layer to show it.

   We copy images onto the canvas when they are loaded, in an animation
   frame, to guarantee no flickering.

   Images are loaded a few in advance using a cache, that is given an array
   of image URLs (it's in shared-utils).

   The URL that is retrieved depends on the raster and the current viewport;
   generally:

   - If the whole raster's source dimensions are known and it is small enough
     (like NRR rain rasters), load an image of the entire raster so it doesn't
     have to change when the user pans or zooms

   - If the user is zoomed out far enough for the whole raster to be visible,
     get an image that only shows the raster, not the whole area around it.

   - If the user is zoomed in, get a WMS-1.0-style image of the whole viewport.

   For performance reasons, the width and height of the image can be made smaller
   if the user's window is too large.

   See ./image-dimensions.ts for those things.
 */

/* Time step starts at 5 minutes, but is doubled until there are less than MAX_STEPS
   total steps. */
const MAX_STEPS = 200;

/* Time per animation step if there is no waiting. */
const FRAME_INTERVAL_MS = 250;

/* How often we check if the image we want to show now has been loaded. */
const ANIMATION_INTERVAL_MS = 25;

interface AnimatedLizardRasterLayerProps {
  isAnimating: boolean;
  raster: Raster;
  wmsBaseUrl: string;
  startMs: number;
  endMs: number;
  currentTime: number;
  setCurrentTime: (d: number) => void; // To update time bar etc
  setWaiting: (waiting: boolean) => void; // To show an hourglass or spinner
  opacity: number;
  beforeId: string;
  rasterStyle?: RasterStyle | null;
}

export function AnimatedLizardRasterLayer({
  isAnimating,
  raster,
  wmsBaseUrl,
  startMs,
  endMs,
  currentTime,
  setCurrentTime,
  setWaiting,
  opacity,
  beforeId,
  rasterStyle,
}: AnimatedLizardRasterLayerProps) {
  const map = useMap();

  const bookkeeping = useRef({
    startMs: -1,
    endMs: -1,
    timeStepMs: -1,
    timeSteps: [] as number[],
  });

  let timeStepMs = 5 * 60 * 1000;
  while ((endMs - startMs) / timeStepMs > MAX_STEPS) {
    timeStepMs *= 2;
  }

  // Update the bookkeeping
  const b = bookkeeping.current;
  if (startMs !== b.startMs || endMs !== b.endMs || timeStepMs !== b.timeStepMs) {
    // (Re-)initialize bookkeeping
    b.startMs = startMs;
    b.endMs = endMs;
    b.timeStepMs = timeStepMs;
    b.timeSteps = [];
    let step = startMs;
    while (step < endMs) {
      b.timeSteps.push(step);
      step += timeStepMs;
    }
  }

  const imageCache = useRef<ImageCache | null>(null);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const lastIndexRendered = useRef<number>(-1);
  const startRerender = () => {
    lastIndexRendered.current = -1;
  };

  // Remove image cache when component dismounts
  useEffect(() => () => imageCache.current?.remove(), []);

  useEffect(() => {
    // A few times per second, if we are animating and we are up to date, update the time step
    const interval = setInterval(() => {
      const b = bookkeeping.current;
      if (isAnimating && b.timeSteps.length > 0) {
        const currentTimeStep = arrayMin(b.timeSteps, (ts) => Math.abs(ts - currentTime));
        if (lastIndexRendered.current === currentTimeStep) {
          const nextTimeStep = (currentTimeStep + 1) % b.timeSteps.length;
          setCurrentTime(b.startMs + nextTimeStep * b.timeStepMs);
        }
      }
    }, FRAME_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isAnimating, currentTime, setCurrentTime]);

  useAnimationFrame(() => {
    /* More often than that, if we are *not* up to date, see if the image
       for the current timestap has been loaded and copy it to the canvas
       if it is. */
    if (canvas.current !== null && imageCache.current !== null) {
      if (lastIndexRendered.current === currentTimeStep) {
        // We are up to date.
        return;
      }
      const image = imageCache.current.getImage(currentTimeStep);
      if (image !== null) {
        /* Loaded, copy it to the canvas */
        setWaiting(false);
        const context = canvas.current.getContext("2d");
        context?.clearRect(0, 0, canvas.current.width, canvas.current.height);
        try {
          context?.drawImage(image, 0, 0, canvas.current.width, canvas.current.height);
        } catch (e) {
          // Sometimes images end up broken, so it goes
          // console.error(e);
        }
        lastIndexRendered.current = currentTimeStep;
      } else {
        /* Not loaded yet, so we are waiting */
        setWaiting(true);
      }
    }
  }, ANIMATION_INTERVAL_MS);

  if (!raster.temporal) {
    console.error(`Raster ${raster.uuid} is not temporal.`);
    return null;
  }

  if (raster.spatial_bounds === null) {
    console.error(
      `Raster ${raster.uuid} cannot be animated because the spatial_bounds field is missing.`
    );
    return null;
  }

  if (!map.current || !bookkeeping.current.timeSteps.length) return null;

  const currentTimeStep = arrayMin(bookkeeping.current.timeSteps, (ts) =>
    Math.abs(ts - currentTime)
  );

  const { width, height, boundingBox } = getOptimalImageDimensions(raster, map.current);

  /* Setup the list of WMS URLs and the image cache, if something
     changed, replace it with a new cache */
  const urls = bookkeeping.current.timeSteps.map((timestep) =>
    makeWmsRequest(wmsBaseUrl, {
      raster,
      width,
      height,
      time: new Date(timestep),
      bbox: boundingBox,
      rasterStyle,
    })
  );

  if (imageCache.current === null || !imageCache.current.isSame(urls, width, height)) {
    if (canvas.current !== null && imageCache.current !== null) {
      /* Clear the canvas */
      const context = canvas.current.getContext("2d");
      context?.clearRect(0, 0, canvas.current.width, canvas.current.height);
    }
    if (imageCache.current) {
      imageCache.current.remove();
    }
    imageCache.current = new ImageCache(urls, width, height);
    startRerender();
  }

  const canvasId = `animating-canvas-${raster.uuid}`;

  if (
    canvas.current === null ||
    canvas.current.width !== width ||
    canvas.current.height !== height
  ) {
    /* First render, or width/height have changed -- get a fresh canvas */
    /* Not very React, I know */
    const oldCanvas = document.getElementById(canvasId);
    if (oldCanvas) {
      oldCanvas.remove();
    }
    canvas.current = document.createElement("canvas");
    canvas.current.id = canvasId;
    canvas.current.width = width;
    canvas.current.height = height;
    canvas.current.style.display = "none";
    document.body.appendChild(canvas.current);
  }

  /* Use normal react-map-gl components to render the canvas on the map */
  return (
    <Source
      key={`${raster.uuid}-${width}x${height}-${JSON.stringify(boundingBox)}`}
      {...({
        type: "canvas",
        canvas: canvasId,
        id: "canvas-source",
        coordinates: boundingBox,
        animate: true,
      } as CanvasSourceSpecification as CanvasSourceAny)}
    >
      <Layer
        id="canvas-layer"
        type="raster"
        source="canvas-source"
        paint={{
          "raster-resampling": "nearest",
          "raster-opacity": opacity,
        }}
        beforeId={beforeId}
      />
    </Source>
  );
}
